import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  PlusIcon,
  TrashIcon,
  InboxIcon,
} from "@heroicons/react/outline";
import { currencyFormat } from "../../../Utils";
import { Field, Label, Switch } from "@headlessui/react";
import FormInput from "../Form/FormInput";
import FormSelect from "../Form/FormSelect";
import FormDate from "../Form/FormDate";
import FormLabel from "../../FormLabel";
import { PAYMENT_FREQUENCY_OPTIONS } from "../../../Constants/business";
import {
  getContributionWage,
  getStatutoryContributionAmount,
} from "../../../Utils/employeeBenefits";

const calculateEndDateMinDate = (
  selectedMonth,
  item,
  dateRestrictions,
  today,
) => {
  if (selectedMonth) {
    if (item.startDate) {
      return new Date(
        Math.max(
          item.startDate.getTime() + 86400000,
          dateRestrictions.minDate.getTime(),
        ),
      );
    }
    return dateRestrictions.minDate;
  }

  if (item.startDate) {
    return new Date(item.startDate.getTime() + 86400000);
  }
  return today;
};

const CollapsibleFormItems = ({
  items,
  setItems,
  typeOptions,
  errors = {},
  setErrors,
  maxItems = 10,
  fieldConfig,
  titleKey,
  addButtonKey,
  maxReachedKey,
  maxMessageKey,
  defaultStartDate,
  employee,
  showTaxableField = false,
  showReduceIncome = false,
  emptyStateTitle = "employees.modal.emptyStateTitle",
  emptyStateSubtitle = "employees.modal.emptyStateSubtitle",
  initiallyCollapsed = false,
  selectedMonth,
  lockStatutoryAmounts = false,
}) => {
  const { t } = useTranslation();

  // Statutory contributions are levied on upah - base salary plus tunjangan
  // tetap - the same base the Salary Table prices them on. JKK also follows
  // the employee's risk level.
  const contributionWage = getContributionWage(employee);
  const riskLevel = employee?.riskLevel;
  const statutoryAmountOf = (type, startDate) =>
    getStatutoryContributionAmount(
      type,
      contributionWage,
      startDate,
      riskLevel,
    );

  // A BPJS deduction, or the employer's JKK/JKM premium, is not the user's to
  // set: the Salary Table works it out every month and ignores whatever is
  // stored, so an editable amount would be an edit that changes nothing. It is
  // shown read-only, and the snapshot kept on the item follows the wage and
  // the start date.
  const isLockedAmount = (item) =>
    lockStatutoryAmounts &&
    statutoryAmountOf(item?.type, item?.startDate) !== null;
  const statutoryAmountBadge =
    fieldConfig.statutoryAmountBadge ||
    "employees.deductions.statutoryAmountBadge";
  const statutoryAmountHint =
    fieldConfig.statutoryAmountHint ||
    "employees.deductions.statutoryAmountHint";

  useEffect(() => {
    if (!lockStatutoryAmounts) return;
    const stale = items.some((item) => {
      const amount = statutoryAmountOf(item.type, item.startDate);
      return amount !== null && amount !== item.amount;
    });
    if (!stale) return;
    setItems((prev) =>
      prev.map((item) => {
        const amount = statutoryAmountOf(item.type, item.startDate);
        return amount === null ? item : { ...item, amount };
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockStatutoryAmounts, contributionWage, riskLevel, items]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  const isMaxItemsReached = items.length >= maxItems;
  const itemErrors = errors[fieldConfig.errorKey] || [];
  const today = new Date();

  const getDateRestrictions = () => {
    if (!selectedMonth) return { minDate: null, maxDate: today };

    const currentYear = new Date().getFullYear();
    const monthNumber = parseInt(selectedMonth, 10) - 1; // Convert MM to 0-based month

    const firstDayOfMonth = new Date(currentYear, monthNumber, 1);
    const lastDayOfMonth = new Date(currentYear, monthNumber + 1, 0);

    return {
      minDate: firstDayOfMonth,
      maxDate: lastDayOfMonth,
    };
  };

  const dateRestrictions = getDateRestrictions();

  const getDefaultStartDate = () => {
    if (!defaultStartDate) return null;

    const employeeStartDate = new Date(defaultStartDate);

    if (selectedMonth && dateRestrictions.minDate) {
      // If employee join date is before the selected month, use first day of selected month
      if (employeeStartDate < dateRestrictions.minDate) {
        return dateRestrictions.minDate;
      }
    }

    return employeeStartDate;
  };

  const defaultItem = {
    name: "",
    description: "",
    amount: 0,
    type: null,
    frequency: selectedMonth
      ? PAYMENT_FREQUENCY_OPTIONS.find((option) => option.value === "ONE_TIME")
      : null,
    isTaxable: showTaxableField ? true : false,
    startDate: getDefaultStartDate(),
    endDate: null,
    reduceIncome: showReduceIncome ? true : false,
  };

  useEffect(() => {
    if (items.length > 0 && !hasInitialized) {
      if (initiallyCollapsed) {
        setExpandedItems(new Set());
      } else {
        setExpandedItems(new Set(items.map((_, index) => index)));
      }
      setHasInitialized(true);
    }
  }, [initiallyCollapsed, items, hasInitialized]);

  const isEmptyItem = (item) =>
    !item.name?.trim() &&
    !item.type &&
    (!item.amount || item.amount <= 0) &&
    !item.frequency?.value;

  const allItemsEmpty = items.every(isEmptyItem);
  const shouldShowEmptyState = allItemsEmpty && items.length === 0;

  const updateItem = (index, updates) => {
    const fieldName = Object.keys(updates)[0];

    setErrors((prev) => {
      if (!prev[fieldConfig.errorKey]?.[index]?.[fieldName]) return prev;

      const newErrors = [...(prev[fieldConfig.errorKey] || [])];
      newErrors[index] = { ...newErrors[index], [fieldName]: null };

      return { ...prev, [fieldConfig.errorKey]: newErrors };
    });

    setItems((prev) => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], ...updates };
      return newItems;
    });
  };

  const handleAddItem = () => {
    if (isMaxItemsReached) return;

    const newIndex = items.length;

    setItems((prev) => [...prev, { ...defaultItem }]);

    setExpandedItems((prev) => new Set([...prev, newIndex]));

    if (items.length === 0) {
      setHasInitialized(true);
    }
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      setItems([]);
      setErrors((prev) => ({ ...prev, [fieldConfig.errorKey]: [] }));
      setExpandedItems(new Set());
      return;
    }

    setItems((prev) => prev.filter((_, i) => i !== index));

    setErrors((prev) => {
      const currentErrors = prev[fieldConfig.errorKey] || [];

      const updatedErrors = currentErrors
        .filter((_, i) => i !== index)
        .map((error, i) => {
          if (!error) return null;
          return error;
        });

      return {
        ...prev,
        [fieldConfig.errorKey]: updatedErrors,
      };
    });

    setExpandedItems((prev) => {
      const newExpanded = new Set();
      prev.forEach((i) => {
        if (i < index) {
          newExpanded.add(i);
        } else if (i > index) {
          newExpanded.add(i - 1);
        }
      });
      return newExpanded;
    });
  };

  const toggleCollapse = (index) => {
    setExpandedItems((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };

  const getTypeLabel = (typeValue) => {
    const option = typeOptions.find((opt) => opt.value === typeValue);
    return option ? t(option.label) : t(typeValue);
  };

  const handleTypeChange = useMemo(
    () => (index) => (option) => {
      // Priced in the period the item itself covers, so an item backdated to
      // before a ceiling change is saved at the ceiling that applied then.
      const amount = statutoryAmountOf(option, items?.[index]?.startDate);

      if (amount === null) {
        return updateItem(index, { type: option });
      }

      updateItem(index, { type: option, amount });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contributionWage, riskLevel, items],
  );

  const EmptyStateDisplay = () => (
    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
      <div className="mx-auto flex items-center justify-center mb-4">
        <InboxIcon className="h-10 w-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {t(emptyStateTitle)}
      </h3>
      <p className="text-sm text-gray-500 mb-6">{t(emptyStateSubtitle)}</p>
      <button
        type="button"
        onClick={handleAddItem}
        disabled={isMaxItemsReached}
        className={clsx(
          "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white",
          isMaxItemsReached
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
        )}
      >
        <PlusIcon className="mr-2 h-4 w-4" />
        {t(addButtonKey)}
      </button>
    </div>
  );

  const formatDate = (date, frequency) => {
    if (!date) return "";
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("default", { month: "short" });
    const year = d.getFullYear();

    return frequency === "ONE_TIME" ? `${day} ${month} ${year}` : `${year}`;
  };

  return (
    <div className="flex flex-col w-full">
      {shouldShowEmptyState ? (
        <EmptyStateDisplay />
      ) : (
        <div className="space-y-4">
          {items?.map((item, index) => {
            const isCollapsed = !expandedItems.has(index);
            const hasErrors = itemErrors?.[index];

            return (
              <div
                key={index}
                className={clsx(
                  "border rounded-lg p-4 bg-white shadow-sm",
                  hasErrors ? "border-red-500" : "border-gray-200",
                )}
              >
                <div className="flex justify-between">
                  <div className="flex-grow">
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() => toggleCollapse(index)}
                        className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                        aria-expanded={!isCollapsed}
                        aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${
                          fieldConfig.itemName
                        }`}
                      >
                        {isCollapsed ? (
                          <ChevronRightIcon className="h-5 w-5" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5" />
                        )}
                      </button>
                      <h3 className="text-sm font-medium text-gray-900 flex-1">
                        {t(titleKey)} {index + 1}
                        {isCollapsed && item?.name && (
                          <span className="text-gray-600 font-normal truncate block">
                            {item.name}
                          </span>
                        )}
                      </h3>
                    </div>

                    {/* Collapsed Summary */}
                    {isCollapsed && (
                      <div className="mt-2 ml-7 flex flex-wrap gap-2">
                        {item?.type && (
                          <span className="bg-purple-100 px-3 py-1 rounded-full text-xs font-medium text-purple-800">
                            {getTypeLabel(item.type.value || item.type)}
                          </span>
                        )}
                        <span className="bg-blue-100 px-3 py-1 rounded-full text-xs font-medium text-blue-800">
                          {item?.amount ? currencyFormat(item.amount) : "-"}
                        </span>
                        {(item.frequency?.value === "ONE_TIME" ||
                          item.frequency?.value === "ANNUAL") &&
                          item.startDate && (
                            <span className="bg-green-100 px-3 py-1 rounded-full text-xs font-medium text-green-800">
                              {formatDate(
                                item.startDate,
                                item.frequency?.value,
                              )}
                            </span>
                          )}
                        {item?.description && (
                          <span className="bg-gray-100 px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                            {item.description.length > 20
                              ? `${item.description.substring(0, 20)}...`
                              : item.description}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    {isCollapsed && hasErrors && (
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemoveItem(index);
                      }}
                      className="p-1 rounded-md"
                      title={items.length === 1 ? "Clear form" : "Remove item"}
                    >
                      <TrashIcon className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                </div>

                {/* Form Fields */}
                {!isCollapsed && (
                  <div className="space-y-4 mt-4">
                    <Field>
                      <FormInput
                        required
                        label={fieldConfig.nameLabel}
                        value={item.name || ""}
                        onChange={(e) =>
                          updateItem(index, { name: e.target.value })
                        }
                        placeholder={t(fieldConfig.namePlaceholder)}
                        error={itemErrors?.[index]?.name}
                      />
                    </Field>

                    <Field>
                      <FormInput
                        label={fieldConfig.descriptionLabel}
                        value={item.description || ""}
                        onChange={(e) =>
                          updateItem(index, { description: e.target.value })
                        }
                        placeholder={t(fieldConfig.descriptionPlaceholder)}
                        hint={t("common.optional")}
                      />
                    </Field>

                    <Field>
                      <FormSelect
                        required
                        searchable
                        label={fieldConfig.typeLabel}
                        value={item.type || null}
                        options={typeOptions}
                        onChange={handleTypeChange(index)}
                        placeholder={t(fieldConfig.typePlaceholder)}
                        error={itemErrors?.[index]?.type}
                      />
                    </Field>

                    <Field>
                      <FormInput
                        required
                        label={fieldConfig.amountLabel}
                        value={item.amount || ""}
                        onValueChange={(values) =>
                          updateItem(index, { amount: values.floatValue || 0 })
                        }
                        disabled={isLockedAmount(item)}
                        hint={
                          isLockedAmount(item)
                            ? statutoryAmountBadge
                            : undefined
                        }
                        placeholder={t(fieldConfig.amountPlaceholder)}
                        error={itemErrors?.[index]?.amount}
                        numberFormat={{ thousandSeparator: true }}
                        prefix="Rp"
                        allowNegative={false}
                      />
                      {isLockedAmount(item) && (
                        <p className="mt-1 text-xs text-gray-500">
                          {t(statutoryAmountHint)}
                        </p>
                      )}
                    </Field>

                    <Field>
                      <FormSelect
                        required
                        label={fieldConfig.frequencyLabel}
                        value={item.frequency || null}
                        options={PAYMENT_FREQUENCY_OPTIONS}
                        onChange={(option) =>
                          updateItem(index, {
                            frequency: option,
                            endDate:
                              option?.value === "ONE_TIME"
                                ? null
                                : item.endDate,
                          })
                        }
                        placeholder={t(fieldConfig.frequencyPlaceholder)}
                        error={itemErrors?.[index]?.frequency}
                      />
                    </Field>

                    {showTaxableField && (
                      <Field as="div" className="mt-1">
                        <div className="flex items-center mt-2 py-2 px-3 border border-gray-300 rounded-md">
                          <Switch
                            checked={item.isTaxable}
                            onChange={() =>
                              updateItem(index, { isTaxable: !item.isTaxable })
                            }
                            className={clsx(
                              item.isTaxable
                                ? "bg-blue-600 hover:bg-blue-700"
                                : "bg-gray-200 hover:bg-gray-300",
                              "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0",
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={clsx(
                                item.isTaxable
                                  ? "translate-x-5"
                                  : "translate-x-0",
                                "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200",
                              )}
                            />
                          </Switch>
                          <Label as="span" className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              <FormLabel
                                label={t(fieldConfig.taxableLabel)}
                                required
                              />
                            </span>
                          </Label>
                        </div>
                      </Field>
                    )}

                    {showReduceIncome && (
                      <Field as="div" className="mt-1">
                        <div className="flex items-center mt-2 py-2 px-3 border border-gray-300 rounded-md">
                          <Switch
                            checked={item.reduceIncome}
                            onChange={() =>
                              updateItem(index, {
                                reduceIncome: !item.reduceIncome,
                              })
                            }
                            className={clsx(
                              item.reduceIncome
                                ? "bg-blue-600 hover:bg-blue-700"
                                : "bg-gray-200 hover:bg-gray-300",
                              "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-0",
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={clsx(
                                item.reduceIncome
                                  ? "translate-x-5"
                                  : "translate-x-0",
                                "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200",
                              )}
                            />
                          </Switch>
                          <Label as="span" className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              <FormLabel
                                label={t(fieldConfig.reduceIncomeLabel)}
                                required
                              />
                            </span>
                          </Label>
                        </div>
                      </Field>
                    )}

                    <Field>
                      <FormDate
                        required
                        label={fieldConfig.startDateLabel}
                        selected={item.startDate || ""}
                        onChange={(date) =>
                          updateItem(index, { startDate: date })
                        }
                        dateFormat="dd/MM/yyyy"
                        placeholderText={t(fieldConfig.startDatePlaceholder)}
                        error={itemErrors?.[index]?.startDate}
                        showMonthDropdown
                        useShortMonthInDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={70}
                        minDate={dateRestrictions.minDate}
                        maxDate={
                          selectedMonth ? dateRestrictions.maxDate : today
                        }
                      />
                    </Field>

                    {item.frequency?.value !== "ONE_TIME" && (
                      <Field>
                        <FormDate
                          label={fieldConfig.endDateLabel}
                          selected={item.endDate || ""}
                          onChange={(date) =>
                            updateItem(index, { endDate: date })
                          }
                          hint={t("common.optional")}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={t(fieldConfig.endDatePlaceholder)}
                          error={itemErrors?.[index]?.endDate}
                          showMonthDropdown
                          useShortMonthInDropdown
                          showYearDropdown
                          scrollableYearDropdown
                          yearDropdownItemNumber={70}
                          onClear={() => updateItem(index, { endDate: null })}
                          minDate={calculateEndDateMinDate(
                            selectedMonth,
                            item,
                            dateRestrictions,
                            today,
                          )}
                          maxDate={
                            selectedMonth ? dateRestrictions.maxDate : undefined
                          }
                        />
                      </Field>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="mt-4">
            <button
              type="button"
              onClick={handleAddItem}
              disabled={isMaxItemsReached}
              className={clsx(
                "inline-flex items-center px-4 py-2 text-sm font-medium",
                isMaxItemsReached
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-blue-600 hover:text-blue-800",
              )}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              {isMaxItemsReached
                ? t(maxReachedKey, { max: maxItems })
                : t(addButtonKey)}
            </button>

            {isMaxItemsReached && (
              <p className="text-xs text-gray-500 mt-1">
                {t(maxMessageKey, { max: maxItems })}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollapsibleFormItems;
